// WebSocket service for real-time messaging

class MessagingWebSocket {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.messageHandlers = new Set();
    this.connectionHandlers = new Set();
    this.typingHandlers = new Set();
    this.isConnected = false;
    this.pendingMessages = [];
  }

  connect() {
    // Use environment variable for WebSocket URL, fallback to localhost
    const WS_BASE_URL = import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8000/ws';
    const wsUrl = `${WS_BASE_URL}/messages/`;

    console.log('Connecting to messaging WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.scheduleReconnect();
    }
  }

  setupEventHandlers() {
    this.ws.onopen = () => {
      console.log('Messaging WebSocket connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Send any pending messages
      this.flushPendingMessages();
      
      // Notify connection handlers
      this.connectionHandlers.forEach(handler => handler(true));
      
      // Start periodic ping to keep connection alive
      this.startPing();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.isConnected = false;
    };

    this.ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      this.isConnected = false;
      this.stopPing();
      
      // Notify connection handlers
      this.connectionHandlers.forEach(handler => handler(false));
      
      // Attempt to reconnect if not a normal closure
      if (event.code !== 1000 && event.code !== 1001) {
        this.scheduleReconnect();
      }
    };
  }

  handleMessage(data) {
    const { type } = data;
    
    switch (type) {
      case 'connection_established':
        console.log('Messaging connection established:', data);
        break;
        
      case 'new_message':
      case 'message_sent':
        // Notify all message handlers
        this.messageHandlers.forEach(handler => handler(data.message));
        break;
        
      case 'message_read_receipt':
        // Handle read receipts
        this.messageHandlers.forEach(handler => {
          if (handler.onReadReceipt) {
            handler.onReadReceipt(data.message_id, data.reader_id);
          }
        });
        break;
        
      case 'typing_indicator':
        // Notify typing handlers
        this.typingHandlers.forEach(handler => handler({
          senderId: data.sender_id,
          senderUsername: data.sender_username,
          isTyping: data.is_typing
        }));
        break;
        
      case 'message_marked_read':
        console.log('Message marked as read:', data.message_id);
        break;
        
      case 'error':
        console.error('WebSocket error message:', data.message);
        break;
        
      case 'pong':
        // Ping response received
        break;
        
      default:
        console.log('Unknown WebSocket message type:', type, data);
    }
  }

  sendMessage(recipientId, content) {
    const message = {
      type: 'send_message',
      recipient_id: recipientId,
      content: content
    };
    
    if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    } else {
      // Queue message to send when reconnected
      this.pendingMessages.push(message);
      console.log('WebSocket not connected, message queued');
      return false;
    }
  }

  markMessageAsRead(messageId) {
    const message = {
      type: 'mark_read',
      message_id: messageId
    };
    
    if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  sendTypingIndicator(recipientId, isTyping) {
    const message = {
      type: 'typing',
      recipient_id: recipientId,
      is_typing: isTyping
    };
    
    if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  flushPendingMessages() {
    while (this.pendingMessages.length > 0 && this.isConnected) {
      const message = this.pendingMessages.shift();
      this.ws.send(JSON.stringify(message));
    }
  }

  startPing() {
    this.pingInterval = setInterval(() => {
      if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Ping every 30 seconds
  }

  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }
    
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    setTimeout(() => {
      this.connect();
    }, delay);
  }

  // Handler management
  onMessage(handler) {
    this.messageHandlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  onConnectionChange(handler) {
    this.connectionHandlers.add(handler);
    
    // Immediately call with current status
    handler(this.isConnected);
    
    // Return unsubscribe function
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  onTyping(handler) {
    this.typingHandlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.typingHandlers.delete(handler);
    };
  }

  disconnect() {
    this.stopPing();
    if (this.ws) {
      this.ws.close(1000, 'User disconnect');
      this.ws = null;
    }
    this.isConnected = false;
    this.messageHandlers.clear();
    this.connectionHandlers.clear();
    this.typingHandlers.clear();
    this.pendingMessages = [];
  }
}

// Create singleton instance
const messagingWebSocket = new MessagingWebSocket();

export default messagingWebSocket;